<template>
  <div v-if="!isDashboard" class="app-breadcrumb">
    <!-- Back '<' button -->
    <button
      class="app-breadcrumb__back"
      type="button"
      aria-label="Go back"
      @click="goBack"
    >
      <v-icon size="22" color="var(--app-text-muted)">mdi-chevron-left</v-icon>
    </button>

    <!-- Breadcrumb trail -->
    <nav class="app-breadcrumb__trail" aria-label="Breadcrumb">
      <template v-for="(crumb, i) in crumbs">
        <button
          v-if="crumb.to && i < crumbs.length - 1"
          :key="`crumb-${i}`"
          type="button"
          class="app-breadcrumb__crumb app-breadcrumb__crumb--link"
          @click="navigate(crumb.to)"
        >
          {{ crumb.label }}
        </button>
        <span
          v-else
          :key="`crumb-${i}`"
          class="app-breadcrumb__crumb app-breadcrumb__crumb--current"
          :aria-current="i === crumbs.length - 1 ? 'page' : null"
        >
          {{ crumb.label }}
        </span>

        <span
          v-if="i < crumbs.length - 1"
          :key="`sep-${i}`"
          class="app-breadcrumb__sep"
          aria-hidden="true"
        >/</span>
      </template>
    </nav>
  </div>
</template>

<script>
import { mapState } from "vuex";

/**
 * Global breadcrumb + back strip.
 *
 * Rendered once per layout (just below the navbar) so every page gets a
 * consistent "where am I / go back" control without each page implementing
 * its own header back button.
 *
 * Trail resolution (in priority order):
 *   1. `root` prop (explicit leading crumb, e.g. the Developer Console).
 *   2. service crumb from the Vuex `currentService` (when inside /service/:key).
 *   3. `route.meta.breadcrumb` — an array of labels appended after the root.
 *   4. the active module name matched from the `menu` prop.
 *   5. a humanized fallback from `route.meta.title` / the route name.
 *
 * The last crumb always renders as the current (non-link) page.
 */
export default {
  name: "AppBreadcrumb",
  props: {
    // The active service's module menu (as built by LayoutConsoleV2). Optional.
    menu: {
      type: Array,
      default: null,
    },
    // Explicit leading crumb, e.g. { label: "Developer Console", to: {...} }.
    // When omitted we derive the leading crumb from the current service.
    root: {
      type: Object,
      default: null,
    },
  },
  computed: {
    ...mapState({
      currentService: (state) => state.service.data.currentService,
    }),
    // The console dashboard is the app root — no breadcrumb/back there.
    isDashboard() {
      return this.$route.name === "PageConsoleDashboard";
    },
    parentCrumb() {
      // Nearest crumb before the current (last) one that has a navigable
      // target — i.e. one level up the breadcrumb hierarchy.
      for (let i = this.crumbs.length - 2; i >= 0; i--) {
        if (this.crumbs[i].to) return this.crumbs[i];
      }
      return null;
    },
    crumbs() {
      const route = this.$route;
      const list = [];

      // 1. Leading crumb -------------------------------------------------------
      if (this.root && this.root.label) {
        list.push({ label: this.root.label, to: this.root.to || null });
      } else if (route.params.serviceKey && this.currentService) {
        list.push({
          label: this.currentService.name,
          to: {
            name: "PageServiceDashboard",
            params: { serviceKey: this.currentService.key },
          },
        });
      }

      // The service dashboard *is* the service crumb — no further trail.
      if (route.name === "PageServiceDashboard") {
        return list.length ? list : [{ label: this.fallbackLabel(route) }];
      }

      // 2. meta.breadcrumb override -------------------------------------------
      // Each entry is a string label, or { label, to }. String labels that
      // match a module in the menu are auto-linked to that module's route, so
      // e.g. "Applications" stays clickable without per-route wiring.
      const metaCrumbs = route.meta && route.meta.breadcrumb;
      if (Array.isArray(metaCrumbs) && metaCrumbs.length) {
        metaCrumbs.forEach((entry) => {
          if (typeof entry === "string") {
            list.push({ label: entry, to: this.menuRouteFor(entry) });
          } else if (entry && entry.label) {
            list.push({
              label: entry.label,
              to: entry.to || this.menuRouteFor(entry.label),
            });
          }
        });
        return list;
      }

      // 3. Active module name from the menu -----------------------------------
      const menuLabel = this.matchMenuLabel(route);
      if (menuLabel) {
        list.push({ label: menuLabel });
        return list;
      }

      // 4. Fallback to meta.title / humanized route name ----------------------
      list.push({ label: this.fallbackLabel(route) });
      return list;
    },
  },
  methods: {
    navigate(to) {
      if (!to) return;
      this.$router.push(to).catch(() => {});
    },
    goBack() {
      // If we arrived from a specific origin page (its route name is carried in
      // route.params.backTo when navigating via an in-app action, e.g. "Next
      // Action" from a detail view), return there — "back to where you came
      // from" — carrying the ids needed to rebuild that page. This wins over
      // the breadcrumb parent so a form opened from a detail view returns to
      // that detail view rather than jumping up to the list.
      const backTo = this.$route.params.backTo;
      if (backTo) {
        this.$router
          .push({ name: backTo, query: this.originQuery() })
          .catch(() => {});
        return;
      }
      // Go up to the nearest parent level in the breadcrumb trail.
      if (this.parentCrumb && this.parentCrumb.to) {
        this.$router.push(this.parentCrumb.to).catch(() => {});
        return;
      }
      // Top-level page (e.g. a service dashboard) — go up to the app home.
      if (this.$route.name !== "PageConsoleDashboard") {
        this.$router.push({ name: "PageConsoleDashboard" }).catch(() => {});
      }
    },
    // Query params worth carrying when returning to an origin page: the record
    // id, the saved view, and the list page number. Only the ones actually
    // present on the current route are forwarded.
    originQuery() {
      const q = this.$route.query;
      const carried = {};
      ["_ausk", "viewId", "currentPageNo"].forEach((k) => {
        if (q[k] != null) carried[k] = q[k];
      });
      return carried;
    },
    matchMenuLabel(route) {
      if (!this.menu) return null;
      const item = this.menu.find((m) => {
        if (!m.route || !m.route.name || m.route.name !== route.name) {
          return false;
        }
        // Generic model pages share one route name and differ only by
        // modelKey — disambiguate on it when both sides carry one.
        if (
          m.route.params &&
          m.route.params.modelKey &&
          route.params &&
          route.params.modelKey
        ) {
          return m.route.params.modelKey === route.params.modelKey;
        }
        return true;
      });
      return item ? item.name : null;
    },
    menuRouteFor(label) {
      if (!this.menu) return null;
      const item = this.menu.find((m) => m.name === label && m.route);
      return item ? this.preserveViewId(item.route) : null;
    },
    // When returning "up" the trail to a list page from one of its detail
    // views, keep the viewId the user came from (e.g. a saved filter view)
    // instead of the menu's default. Only kicks in when both the current
    // route and the target list route are viewId-aware, so unrelated routes
    // are left untouched.
    preserveViewId(route) {
      const currentViewId = this.$route.query.viewId;
      if (
        currentViewId == null ||
        !route.query ||
        route.query.viewId == null
      ) {
        return route;
      }
      return { ...route, query: { ...route.query, viewId: currentViewId } };
    },
    fallbackLabel(route) {
      if (route.meta && route.meta.title) return route.meta.title;
      return this.humanize(route.name || "");
    },
    humanize(name) {
      if (!name) return "";
      return name
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
  },
};
</script>

<style scoped>
/* No `.dark-mode` block. Every colour below resolves through var(--app-*),
   which theme-dark.css already flips on <html>; a second dark-only copy is what
   let this file's palette drift from the rest of the app before. */

/* The strip does NOT stick. It is the header row of the page panel welded
   beneath it (theme-glass.css §2), so it scrolls away with the page it labels
   — a sticky header on top of a welded panel would have torn the seam open as
   soon as the page moved, leaving the strip floating over its own sheet.

   It keeps the frost because the panel it belongs to is frosted; the blur
   below therefore matches the sheet's --glass-blur-panel, not the navbar's
   stronger --glass-blur. */
.app-breadcrumb {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 14px 4px;
  padding: 8px 14px;
  border: 1px solid var(--app-navbar-glass-border);
  /* -lg to match the page sheet's corners: theme-glass.css §2 welds the two
     into one panel, and mismatched radii would put a 16px corner on top of a
     20px one. The rounded-bottom + gap this rule describes is the standalone
     fallback, for a page with no container and for browsers without :has(). */
  border-radius: var(--glass-radius-lg);
  background: var(--app-navbar);
  box-shadow:
    var(--glass-lift-float),
    inset 0 1px 0 var(--app-inner-highlight);
  font-family: "Lato", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif;
}

/* Opaque baseline above, frost here — see the note in theme-glass.css §3. */
@supports ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
  .app-breadcrumb {
    background: var(--app-navbar-glass);
    -webkit-backdrop-filter: blur(var(--glass-blur-panel)) saturate(var(--glass-saturate));
    backdrop-filter: blur(var(--glass-blur-panel)) saturate(var(--glass-saturate));
  }
}

.app-breadcrumb__back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  background: var(--app-card);
  border: 1px solid var(--app-border);
  border-radius: 10px;
  cursor: pointer;
  box-shadow: 0 1px 2px var(--app-shadow-soft);
  transition: background-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.app-breadcrumb__back:hover {
  background: var(--app-elevated);
  box-shadow: 0 2px 8px var(--app-shadow);
  transform: translateX(-1px);
}

.app-breadcrumb__back:active {
  transform: translateX(-1px) scale(0.96);
}

.app-breadcrumb__back:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--app-ring);
}

.app-breadcrumb__trail {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  row-gap: 2px;
  min-width: 0;
}

.app-breadcrumb__crumb {
  font-size: 0.82rem;
  font-weight: 500;
  line-height: 1.2;
  background: none;
  border: none;
  padding: 3px 7px;
  white-space: nowrap;
}

.app-breadcrumb__crumb--link {
  color: var(--app-text-muted);
  cursor: pointer;
  border-radius: 999px;
  transition: color 0.15s ease, background-color 0.15s ease;
}

.app-breadcrumb__crumb--link:hover {
  color: var(--app-accent);
  background: var(--app-accent-soft);
}

.app-breadcrumb__crumb--current {
  color: var(--app-text);
  font-weight: 700;
}

.app-breadcrumb__sep {
  margin: 0 4px;
  color: var(--app-text-faint);
  font-size: 0.9rem;
  font-weight: 400;
  flex-shrink: 0;
  user-select: none;
}

@media (max-width: 599px) {
  /* Matches the page sheet's mobile inset and its smaller corner. */
  .app-breadcrumb {
    margin: 0 8px 4px;
    padding: 8px 10px;
    gap: 8px;
    border-radius: var(--glass-radius);
  }
}
</style>
